/**
 * Normalizes emoji presentation by forcing text-style (black/white) rendering
 * for all emojis by adding the text variation selector (U+FE0E).
 *
 * This ensures emojis render in monochrome using the Noto Emoji font,
 * matching the clean aesthetic of the application.
 *
 * Variation selectors:
 * - ️ (U+FE0F) = emoji presentation (color)
 * - ︎ (U+FE0E) = text presentation (black/white)
 */

const TEXT_VARIATION_SELECTOR = '\uFE0E'

// Comprehensive emoji regex covering all Unicode emoji ranges
const EMOJI_PATTERN =
  /([\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{231A}-\u{23FF}\u{2B50}\u{2B55}\u{1F900}-\u{1F9FF}\u{1FA70}-\u{1FAFF}\u{1F004}\u{1F0CF}\u{1F170}-\u{1F251}])/gu

/**
 * Normalizes all emojis in text to use text-style presentation (black/white).
 * Strips any existing variation selectors and adds text presentation selector.
 *
 * @param text - Input text that may contain emojis
 * @returns Text with all emojis forced to text-style presentation
 *
 * @example
 * normalizeEmojis('Settings ⚙️') // Returns 'Settings ⚙︎'
 * normalizeEmojis('☀️ Sunny day') // Returns '☀︎ Sunny day'
 */
export function normalizeEmojis(text: string): string {
  // First, remove any existing variation selectors (both emoji and text)
  const cleaned = text.replace(/[\uFE0E\uFE0F]/g, '')

  // Then add text variation selector to all emojis
  return cleaned.replace(EMOJI_PATTERN, (match) => {
    return match + TEXT_VARIATION_SELECTOR
  })
}
